import type { Cadeia, PalavraCanonica, PalavraColocada } from '../tipos';
import { paraComparacao } from '../lexico/normalizacao';
import type { EstadoLetra } from './avaliacao';
import { acertou, avaliar } from './avaliacao';

/**
 * A Trama sobre a cadeia (§23).
 *
 * O jogo é o Entreletras inteiro — palavra horizontal, vertical cruzando,
 * âncora, encaixe espacial —, com uma pergunta a mais por cima: **qual é a
 * palavra do dia?** Cada palavra-base que o jogador confirma é também um
 * palpite, e as letras dela ganham verde, amarelo ou cinza na própria célula
 * do tabuleiro.
 *
 * Isso faz o jogador querer duas coisas ao mesmo tempo, que nem sempre andam
 * juntas: a palavra tem de caber no espaço que sobrou **e** informar sobre a
 * resposta. É a tensão do modo.
 *
 * Não há limite de tentativas. Com a âncora mandando na letra obrigatória, um
 * teto fixo tornaria alguns dias impossíveis por posicionamento — e o jogador
 * não teria como saber que foi isso. O placar é quantas palavras foram
 * precisas (§23.1).
 */

/** As palavras que o jogador escreveu: as bases da cadeia, sem as pontes. */
export function palavrasDoJogador(cadeia: Cadeia): readonly PalavraColocada[] {
  return cadeia.palavras.filter((p) => p.papel === 'base');
}

/** Avaliação de cada palavra-base contra a resposta do dia. */
export function marcasDaCadeia(
  cadeia: Cadeia,
  resposta: PalavraCanonica | undefined,
): ReadonlyMap<string, readonly EstadoLetra[]> {
  const marcas = new Map<string, readonly EstadoLetra[]>();
  if (resposta === undefined || resposta === '') return marcas;

  for (const palavra of palavrasDoJogador(cadeia)) {
    marcas.set(palavra.id, avaliar(palavra.texto, resposta));
  }
  return marcas;
}

/** O jogador já escreveu a palavra do dia? */
export function descobriu(cadeia: Cadeia, resposta: PalavraCanonica | undefined): boolean {
  if (resposta === undefined || resposta === '') return false;
  const alvo = paraComparacao(resposta);
  return palavrasDoJogador(cadeia).some((p) => paraComparacao(p.texto) === alvo);
}

/**
 * A palavra que acabou de ser confirmada é a resposta?
 *
 * Separado de `descobriu` porque o redutor precisa saber da jogada, não da
 * cadeia inteira — e porque `avaliar` já responde isso.
 */
export function ehAResposta(
  palavra: PalavraCanonica,
  resposta: PalavraCanonica | undefined,
): boolean {
  if (resposta === undefined || resposta === '') return false;
  return acertou(avaliar(palavra, resposta));
}

/** Placar do dia: quantas palavras o jogador precisou escrever (§23.1). */
export function palavrasUsadas(cadeia: Cadeia): number {
  return palavrasDoJogador(cadeia).length;
}
